import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@monksflow/monks-ui';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import type { AsyncImageProps } from './AsyncImage.types';

const CONTAINER = 'flex size-full items-center justify-center overflow-hidden';

/** The `dark` variant is for hosts sitting on an inverted surface. */
const SURFACE = {
  light: 'bg-muted text-muted-foreground',
  dark: 'bg-background/10 text-background/50',
} as const;

/**
 * Fills its host box with an asset image, showing a spinner while the download
 * URL resolves and a labelled placeholder when the asset has none.
 *
 * @example
 * ```tsx
 * <AsyncImage assetId={image?.assetId} alt="Campaign header image" placeholderText="No image" />
 * ```
 */
export const AsyncImage = memo(function AsyncImage({
  assetId,
  alt,
  variant = 'light',
  placeholderText,
}: AsyncImageProps) {
  const { t } = useTranslation('contentTest');

  const { downloadUrl, isLoading } = useGetDownloadUrl(assetId);

  if (isLoading) {
    return (
      <div
        className={`${CONTAINER} ${SURFACE[variant]}`}
        role="img"
        aria-label={t('ideationWidget.asyncImage.loading', { alt })}
      >
        <Spinner size="lg" aria-hidden="true" />
      </div>
    );
  }

  if (!downloadUrl) {
    return (
      <div
        className={`${CONTAINER} ${SURFACE[variant]} flex-col gap-2`}
        role="img"
        aria-label={placeholderText}
      >
        {placeholderText && <span>{placeholderText}</span>}
      </div>
    );
  }

  return (
    <div className={CONTAINER}>
      <img src={downloadUrl} alt={alt} className="size-full object-cover" />
    </div>
  );
});
